import React, { useEffect, useMemo, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const BASE_PRICE_BY_AREA = {
  150: 21000,
  200: 30000,
};

const SEPARATION_AMOUNT = 300; // separación fija

const parseNumberOrNull = (value) => {
  const raw = String(value ?? "").trim();
  if (raw === "") return null;
  const n = Number(raw.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};

const LotDetailsPanel = ({
  lot,
  onClose,
  onContactInterest,
  onUpdateLot,
  canEditStatus = true, // por ahora true
}) => {
  if (!lot) return null;

  const getStatusBadge = (status) => {
    const badges = {
      available: { text: "Disponible", className: "bg-success/20 text-success border-success" },
      reserved: { text: "Reservado", className: "bg-warning/20 text-warning border-warning" },
      sold: { text: "Vendido", className: "bg-error/20 text-error border-error" },
    };
    const badge = badges?.[status] || badges?.available;
    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-lg text-xs md:text-sm font-medium border ${badge?.className}`}
      >
        {badge?.text}
      </span>
    );
  };

  // =========================
  // ✅ Estados editables
  // =========================
  const [editStatus, setEditStatus] = useState(lot?.status || "available");
  const [editCustomerName, setEditCustomerName] = useState(lot?.customer_name || "");
  const [editDealAmount, setEditDealAmount] = useState(
    lot?.deal_amount === null || lot?.deal_amount === undefined ? "" : String(lot.deal_amount)
  );

  // ✅ Usa area_m2 real si viene de BD
  const [editArea, setEditArea] = useState(lot?.area_m2 ?? null); // number | null

  // ✅ descuento + precio editable
  const [discountEnabled, setDiscountEnabled] = useState(!!lot?.discount_enabled);
  const [editPriceTotal, setEditPriceTotal] = useState(
    lot?.price_total === null || lot?.price_total === undefined ? "" : String(lot.price_total)
  );

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditStatus(lot?.status || "available");
    setEditCustomerName(lot?.customer_name || "");
    setEditDealAmount(lot?.deal_amount === null || lot?.deal_amount === undefined ? "" : String(lot.deal_amount));

    setEditArea(lot?.area_m2 ?? null);

    setDiscountEnabled(!!lot?.discount_enabled);
    setEditPriceTotal(lot?.price_total === null || lot?.price_total === undefined ? "" : String(lot.price_total));
  }, [lot?.id]);

  // =========================
  // ✅ Precio base automático por área
  // =========================
  const basePrice = useMemo(() => {
    const a = Number(editArea || 0);
    if (!a) return 0;
    return BASE_PRICE_BY_AREA?.[a] || 0;
  }, [editArea]);

  // ✅ Precio final mostrado (si descuento ON usa input; si OFF usa base)
  const computedFinalPrice = useMemo(() => {
    const a = Number(editArea || 0);
    if (!a) return 0;

    if (!discountEnabled) return basePrice;

    const n = parseNumberOrNull(editPriceTotal);
    if (n === null) return basePrice; // si vacío, cae al base
    if (Number.isNaN(n)) return basePrice;
    return n;
  }, [discountEnabled, editPriceTotal, basePrice, editArea]);

  // =========================
  // ✅ Datos “ficha”
  // =========================
  const safeArea = editArea ? Number(editArea) : "-";
  const safeDimensions = lot?.dimensions ?? "-";
  const safeOrientation = lot?.orientation ?? "-";
  const safeLocation = lot?.location ?? "-";
  const safeFeatures = Array.isArray(lot?.features) ? lot.features : [];
  const safeAmenities = Array.isArray(lot?.nearbyAmenities) ? lot.nearbyAmenities : [];

  // Financing
  const calculateFinancing = (price, downPaymentPercent, months = 60, annualRate = 8.5) => {
    if (!price || price <= 0) {
      return { downPayment: 0, monthlyPayment: 0, totalPayment: 0, totalInterest: 0 };
    }
    const downPayment = price * (downPaymentPercent / 100);
    const financedAmount = price - downPayment;
    const monthlyRate = annualRate / 100 / 12;
    const monthlyPayment =
      financedAmount *
      ((monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1));
    return {
      downPayment,
      monthlyPayment,
      totalPayment: monthlyPayment * months + downPayment,
      totalInterest: monthlyPayment * months + downPayment - price,
    };
  };

  const financing20 = useMemo(() => calculateFinancing(computedFinalPrice, 20), [computedFinalPrice]);
  const financing30 = useMemo(() => calculateFinancing(computedFinalPrice, 30), [computedFinalPrice]);

  // =========================
  // ✅ Guardar cambios a BD
  // =========================
  const handleSaveStatus = async () => {
    if (!canEditStatus) return;

    if (!onUpdateLot) {
      alert("Falta conectar onUpdateLot desde index.jsx");
      return;
    }

    // deal_amount parse
    const amount = parseNumberOrNull(editDealAmount);
    if (Number.isNaN(amount)) {
      alert("Monto inválido. Usa solo números (ej: 1500 o 1500.50).");
      return;
    }

    // area_m2 valida (por ahora permitimos null, pero para cálculos conviene que exista)
    const area_m2 = editArea ? Number(editArea) : null;

    // price_total:
    // - Si descuento OFF => lo dejamos en null para que se derive por área siempre (recomendado)
    // - Si descuento ON  => guardamos el número
    let price_total = null;

    if (discountEnabled) {
      const p = parseNumberOrNull(editPriceTotal);
      if (p === null || Number.isNaN(p)) {
        alert("Precio inválido. Si activas descuento, escribe un precio válido.");
        return;
      }
      price_total = p;
    } else {
      price_total = null;
    }

    setSaving(true);
    try {
      await onUpdateLot({
        id: lot?.id ?? lot?.number, // lot_code
        status: editStatus,
        customer_name: editCustomerName?.trim(),
        deal_amount: amount,
        area_m2,
        price_total,
        discount_enabled: !!discountEnabled,
      });
    } catch (e) {
      console.error(e);
      alert(e?.message || "Error guardando cambios");
    } finally {
      setSaving(false);
    }
  };

  const labelForAmount =
    editStatus === "reserved" ? "Monto de separación" : editStatus === "sold" ? "Precio de compra" : "Monto";
  const placeholderForAmount =
    editStatus === "reserved" ? "Ej: 300" : editStatus === "sold" ? "Ej: 35000" : "Ej: 1500";

  return (
    <div className="bg-card rounded-xl shadow-lg overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-xl md:text-2xl font-semibold mb-2">Lote {lot?.number}</h3>
          {getStatusBadge(lot?.status)}
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-xl hover:bg-muted transition-smooth flex items-center justify-center touch-target focus-ring"
        >
          <Icon name="X" size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4 md:p-6">
        {/* ✅ Estado / Reserva / Venta */}
        <div className="mb-6 md:mb-8">
          <div className="p-4 bg-muted/40 border border-border rounded-xl">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Icon name="ClipboardList" size={18} className="text-primary" />
                <h4 className="text-base md:text-lg font-semibold">Estado / Reserva / Venta</h4>
              </div>

              {!canEditStatus && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-card text-muted-foreground">
                  Solo lectura
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3">
              {/* Área */}
              <div>
                <div className="text-xs text-muted-foreground mb-1">Área del lote (m²)</div>
                <select
                  className="w-full border rounded-xl px-3 py-2 bg-background"
                  value={editArea === null ? "" : String(editArea)}
                  onChange={(e) => setEditArea(e.target.value === "" ? null : Number(e.target.value))}
                  disabled={!canEditStatus}
                >
                  <option value="">Sin definir</option>
                  <option value="150">150 m² (S/ 21,000)</option>
                  <option value="200">200 m² (S/ 30,000)</option>
                </select>

                {!editArea && (
                  <div className="text-[11px] mt-1 text-warning">
                    ⚠️ Si defines el área, el sistema calcula precio y financiamiento automáticamente.
                  </div>
                )}
              </div>

              {/* Estado */}
              <div>
                <div className="text-xs text-muted-foreground mb-1">Estado</div>
                <select
                  className="w-full border rounded-xl px-3 py-2 bg-background"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  disabled={!canEditStatus}
                >
                  <option value="available">Disponible</option>
                  <option value="reserved">Reservado</option>
                  <option value="sold">Vendido</option>
                </select>
              </div>

              {/* Nombre */}
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  {editStatus === "reserved"
                    ? "Nombre del que reservó"
                    : editStatus === "sold"
                    ? "Nombre del comprador"
                    : "Nombre"}
                </div>
                <input
                  className="w-full border rounded-xl px-3 py-2 bg-background"
                  value={editCustomerName}
                  onChange={(e) => setEditCustomerName(e.target.value)}
                  placeholder="Ej: Juan Pérez"
                  disabled={!canEditStatus}
                />
              </div>

              {/* Monto reserva/compra */}
              <div>
                <div className="text-xs text-muted-foreground mb-1">{labelForAmount}</div>
                <input
                  className="w-full border rounded-xl px-3 py-2 bg-background"
                  value={editDealAmount}
                  onChange={(e) => setEditDealAmount(e.target.value)}
                  placeholder={placeholderForAmount}
                  inputMode="decimal"
                  disabled={!canEditStatus}
                />
              </div>

              {/* Descuento */}
              <div className="p-3 rounded-xl border bg-background">
                <label className="flex items-center gap-2 text-sm select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={discountEnabled}
                    onChange={(e) => setDiscountEnabled(e.target.checked)}
                    disabled={!canEditStatus || !editArea}
                  />
                  Activar descuento (permitir modificar precio)
                </label>

                <div className="text-[11px] text-muted-foreground mt-1">
                  Precio base: <b>S/ {basePrice.toLocaleString("es-PE")}</b> • Precio final:{" "}
                  <b>S/ {computedFinalPrice.toLocaleString("es-PE")}</b>
                </div>

                {discountEnabled && (
                  <div className="mt-2">
                    <div className="text-xs text-muted-foreground mb-1">Precio final con descuento (S/)</div>
                    <input
                      className="w-full border rounded-xl px-3 py-2 bg-background"
                      value={editPriceTotal}
                      onChange={(e) => setEditPriceTotal(e.target.value)}
                      placeholder={editArea ? `Ej: ${Math.max(basePrice - 1000, 0)}` : "Ej: 29000"}
                      inputMode="decimal"
                      disabled={!canEditStatus}
                    />
                  </div>
                )}
              </div>

              <Button
                variant="default"
                fullWidth
                iconName="Save"
                iconPosition="left"
                onClick={handleSaveStatus}
                disabled={!canEditStatus || saving || !onUpdateLot}
              >
                {saving ? "Guardando..." : "Guardar cambios"}
              </Button>

              {!onUpdateLot && (
                <div className="text-[11px] text-muted-foreground">
                  Falta conectar <b>onUpdateLot</b> desde <b>interactive-floor-plans/index.jsx</b>.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Precio Total */}
        <div className="mb-6 md:mb-8">
          <div className="text-sm text-muted-foreground mb-2">Precio Total</div>
          <div className="text-3xl md:text-4xl font-bold text-primary mb-3">
            S/ {computedFinalPrice.toLocaleString("es-PE")}
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-3 py-1 bg-success/10 text-success rounded-lg text-xs md:text-sm font-medium">
              Separación: S/ {SEPARATION_AMOUNT}
            </span>
            <span className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-xs md:text-sm font-medium">
              Hasta 60 meses
            </span>
          </div>
        </div>

        {/* Especificaciones */}
        <div className="mb-6 md:mb-8">
          <h4 className="text-base md:text-lg font-semibold mb-4">Especificaciones</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Maximize2" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Área</div>
                <div className="font-medium">{safeArea} {safeArea === "-" ? "" : "m²"}</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Ruler" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Dimensiones</div>
                <div className="font-medium">{safeDimensions}</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Compass" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Orientación</div>
                <div className="font-medium">{safeOrientation}</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="MapPin" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Ubicación</div>
                <div className="font-medium">{safeLocation}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Características */}
        <div className="mb-6 md:mb-8">
          <h4 className="text-base md:text-lg font-semibold mb-4">Características</h4>
          <div className="grid grid-cols-1 gap-3">
            {safeFeatures.length === 0 ? (
              <div className="text-sm text-muted-foreground">No hay características registradas.</div>
            ) : (
              safeFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
                  <span className="text-sm md:text-base">{feature}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Servicios cercanos */}
        <div className="mb-6 md:mb-8">
          <h4 className="text-base md:text-lg font-semibold mb-4">Servicios Cercanos</h4>
          <div className="grid grid-cols-1 gap-3">
            {safeAmenities.length === 0 ? (
              <div className="text-sm text-muted-foreground">No hay servicios cercanos registrados.</div>
            ) : (
              safeAmenities.map((amenity, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Icon name="MapPin" size={18} color="var(--color-primary)" className="mt-1" />
                  <div>
                    <div className="font-medium text-sm md:text-base">{amenity?.name}</div>
                    <div className="text-xs md:text-sm text-muted-foreground">{amenity?.distance}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Financiamiento */}
        <div className="mb-6 md:mb-8">
          <h4 className="text-base md:text-lg font-semibold mb-4">Planes de Financiamiento</h4>

          <div className="space-y-3">
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CreditCard" size={18} className="text-primary" />
                <div className="font-medium text-primary">Financiamiento Directo (20%)</div>
              </div>
              <div className="text-sm text-muted-foreground mb-3">
                Inicial 20% + 60 cuotas mensuales al 8.5% anual
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Inicial:</span>
                  <span className="font-semibold text-foreground ml-2">
                    S/ {financing20.downPayment.toLocaleString("es-PE", { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Cuota:</span>
                  <span className="font-semibold text-primary ml-2">
                    S/ {financing20.monthlyPayment.toLocaleString("es-PE", { maximumFractionDigits: 0 })}/mes
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-accent/5 border border-accent/20 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Building2" size={18} className="text-accent" />
                <div className="font-medium text-accent">Crédito Privado (30%)</div>
              </div>
              <div className="text-sm text-muted-foreground mb-3">
                Inicial 30% + 60 cuotas mensuales al 8.5% anual
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Inicial:</span>
                  <span className="font-semibold text-foreground ml-2">
                    S/ {financing30.downPayment.toLocaleString("es-PE", { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Cuota:</span>
                  <span className="font-semibold text-accent ml-2">
                    S/ {financing30.monthlyPayment.toLocaleString("es-PE", { maximumFractionDigits: 0 })}/mes
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-success/5 border border-success/20 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Wallet" size={18} className="text-success" />
                <div className="font-medium text-success">Pago al Contado (15% descuento)</div>
              </div>
              <div className="text-sm text-muted-foreground mb-3">Pago único con descuento especial</div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Precio final:</span>
                <span className="text-lg font-bold text-success">
                  S/ {(computedFinalPrice * 0.85).toLocaleString("es-PE", { maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <Icon name="Info" size={16} className="text-primary" />
              <span className="text-muted-foreground">
                Separa tu lote con solo{" "}
                <span className="font-semibold text-foreground">S/ {SEPARATION_AMOUNT}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="p-4 md:p-6 border-t border-border space-y-3">
        <Button variant="default" fullWidth iconName="Phone" iconPosition="left" onClick={() => onContactInterest(lot)}>
          Contactar Asesor
        </Button>

        <Button
          variant="outline"
          fullWidth
          iconName="Share2"
          iconPosition="left"
          onClick={() => {
            const shareText = `Lote ${lot?.number} - ${editArea || "?"} m² - S/ ${computedFinalPrice.toLocaleString(
              "es-PE"
            )} - Separación desde S/ ${SEPARATION_AMOUNT}`;
            navigator.clipboard?.writeText(shareText);
          }}
        >
          Compartir Lote
        </Button>
      </div>
    </div>
  );
};

export default LotDetailsPanel;
